import { useEffect, useMemo, useRef, useState } from "react";
import { Snackbar, Alert } from "@mui/material";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { getImageUrl } from "../api/masterApi";
import {
  getReferenceUserId,
  shouldScopeToOwnUserData,
  isAdminRole,
} from "../utils/auth";
import DropdownMultiSelect from "./DropdownMultiSelect";
import "./MasterCrud.css";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const FILE_FIELD_TYPES = new Set([
  "file",
  "image",
  "files",
  "video",
  "videos",
  "document",
  "documents",
]);

function isVerificationFieldLocked(field) {
  return Boolean(field?.verificationField) && !isAdminRole();
}

const LONG_TEXT_KEY_PATTERN = /description|content|details|notes|address|_text$/i;

const MB = 1024 * 1024;
const MAX_IMAGE_MB = 10;
const MAX_VIDEO_MB = 100;
// The reverse proxy rejects oversized requests before they reach the API, and
// that rejection reaches the browser as an opaque network/CORS error, so the
// whole payload is checked here while a useful message can still be shown.
// This must stay below the proxy's client_max_body_size (see backend/nginx-api.conf);
// raise REACT_APP_MAX_UPLOAD_MB only after the proxy has been raised to match.
const MAX_REQUEST_MB = Number(process.env.REACT_APP_MAX_UPLOAD_MB || 45);

function mb(bytes) {
  return (bytes / MB).toFixed(1);
}

function fileSizeError(file) {
  // A single file can never be split across requests, so the request ceiling is
  // a harder limit than the per-type one and needs its own message.
  if (file.size > MAX_REQUEST_MB * MB) {
    return `"${file.name}" is ${mb(file.size)} MB, which is over the ${MAX_REQUEST_MB} MB limit for a single upload. Please compress it or upload a smaller version.`;
  }
  if (file.type.startsWith("video/") && file.size > MAX_VIDEO_MB * MB) {
    return `"${file.name}" is ${mb(file.size)} MB. Videos must be under ${MAX_VIDEO_MB} MB.`;
  }
  if (file.type.startsWith("image/") && file.size > MAX_IMAGE_MB * MB) {
    return `"${file.name}" is ${mb(file.size)} MB. Images must be under ${MAX_IMAGE_MB} MB.`;
  }
  return null;
}

// Long prose stretches a row to dozens of lines, so those cells are clamped to two lines.
function isClampedColumn(col) {
  if (col.clamp !== undefined) return Boolean(col.clamp);
  if (col.type === "html") return true;
  if (col.type) return false;
  return LONG_TEXT_KEY_PATTERN.test(col.key || "");
}

function stripHtml(html = "") {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
}

function basename(value = "") {
  return String(value).split("/").pop();
}

function asFileList(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return [value].filter(Boolean);
}

function parseStringList(value) {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry).trim()).filter(Boolean);
  }
  if (value == null || value === "") return [];
  const raw = String(value).trim();
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map((entry) => String(entry).trim()).filter(Boolean);
    }
  } catch (_err) {
    // Treat as newline / comma separated text.
  }
  return raw
    .split(/[\n,]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function stringListToText(value) {
  return parseStringList(value).join("\n");
}

function buildEmptyForm(fields) {
  const form = {};
  fields.forEach((field) => {
    if (field.type === "checkbox") form[field.name] = field.defaultValue !== undefined ? Boolean(field.defaultValue) : true;
    else if (field.type === "radio") form[field.name] = field.defaultValue ?? field.options?.[0]?.value ?? "";
    else if (field.type === "multiselect") form[field.name] = [];
    else if (field.type === "stringlist") form[field.name] = "";
    else if (FILE_FIELD_TYPES.has(field.type)) form[field.name] = [];
    else form[field.name] = field.defaultValue ?? "";
  });
  return form;
}

function isFieldVisible(field, form) {
  if (field.hidden) return false;
  if (!field.showWhen) return true;
  return String(form[field.showWhen.field] || "") === String(field.showWhen.equals);
}

/** Walk option parent chain to fill missing inherit values (e.g. city → state → country). */
function resolveInheritedFromOptions(options, selectedValue, inheritFields) {
  const byId = new Map(options.map((opt) => [String(opt.value), opt]));
  const keys = inheritFields.map((spec) => spec.from);
  const result = {};
  const pending = new Set(keys);
  let current = byId.get(String(selectedValue));
  let guard = 0;

  while (current && pending.size > 0 && guard < 20) {
    for (const key of [...pending]) {
      const raw = current[key];
      if (raw != null && raw !== "") {
        result[key] = raw;
        pending.delete(key);
      }
    }
    const parentId = current.parent_location_id ?? current.parentId;
    current = parentId != null && parentId !== "" ? byId.get(String(parentId)) : null;
    guard += 1;
  }

  return result;
}

export default function MasterCrudPage({
  title,
  description,
  idKey,
  fields,
  columns,
  api,
  listLabel = "records",
  hasImage = false,
  imageField = null,
  useJson = false,
  onAfterSave,
  loadForEdit,
  loadExtraOptions,
  /** When true, non-admin users only see/create/edit rows for their linked user_id */
  scopeToCurrentUser = false,
  userIdField = "user_id",
}) {
  const isUserScoped = Boolean(scopeToCurrentUser && shouldScopeToOwnUserData());
  const scopedUserId = isUserScoped ? getReferenceUserId() : null;

  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(() => {
    const initial = buildEmptyForm(fields);
    if (scopedUserId != null) initial[userIdField] = String(scopedUserId);
    return initial;
  });
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [fileUploads, setFileUploads] = useState({});
  const [existingFiles, setExistingFiles] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [optionMaps, setOptionMaps] = useState({});
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const fileInputRef = useRef(null);
  const multiFileRefs = useRef({});
  const inheritTokenRef = useRef(0);

  const visibleFields = useMemo(
    () =>
      fields.map((field) => {
        if (!isUserScoped || field.name !== userIdField) return field;
        return { ...field, hidden: true, required: false };
      }),
    [fields, isUserScoped, userIdField]
  );

  const formFields = useMemo(() => {
    if (isAdminRole()) return visibleFields;
    return visibleFields.filter((field) => !field.verificationField);
  }, [visibleFields]);

  const lockedVerificationFields = useMemo(() => {
    if (isAdminRole()) return [];
    return visibleFields.filter(
      (field) => field.verificationField && isFieldVisible(field, form)
    );
  }, [visibleFields, form]);

  const verificationBadges = useMemo(() => {
    return lockedVerificationFields
      .filter((field) => field.name.endsWith("_verified") && Boolean(form[field.name]))
      .map((field) => {
        const base = field.name.replace(/_verified$/, "");
        const certKey = `${base}_verification_certificate`;
        const certFromForm = Array.isArray(form[certKey])
          ? form[certKey][0]
          : form[certKey];
        const certFromFiles = (existingFiles[certKey] || [])[0];
        return {
          key: field.name,
          label: String(field.label || "Verified")
            .replace(/^Is\s+/i, "")
            .trim(),
          by: form[`${base}_verification_by`] || "",
          date: String(form[`${base}_verification_date`] || "").slice(0, 10),
          certificate: certFromForm || certFromFiles || "",
        };
      });
  }, [lockedVerificationFields, form, existingFiles]);

  const renderLockedVerificationField = (field) => {
    const locked = true;
    const value = form[field.name];

    if (field.name.endsWith("_verified") || field.type === "checkbox") {
      return (
        <select id={field.name} value={value ? "1" : "0"} disabled>
          <option value="0">No</option>
          <option value="1">Yes</option>
        </select>
      );
    }

    if (FILE_FIELD_TYPES.has(field.type)) {
      const existing = existingFiles[field.name] || [];
      return (
        <div className="master-form__files">
          <input
            id={field.name}
            type="file"
            accept={field.accept || "image/*,application/pdf,.pdf"}
            disabled={locked}
          />
          {existing.length > 0 && (
            <div className="master-form__file-list master-verify-existing">
              {existing.map((file, index) => (
                <a
                  key={`${field.name}-${index}`}
                  href={getImageUrl(file)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {basename(file)}
                </a>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (field.type === "date") {
      return (
        <input
          id={field.name}
          type="date"
          value={String(value || "").slice(0, 10)}
          readOnly
          disabled
        />
      );
    }

    return (
      <input
        id={field.name}
        type="text"
        value={value || ""}
        placeholder={field.placeholder || ""}
        readOnly
        disabled
      />
    );
  };

  const visibleColumns = useMemo(() => {
    if (!isUserScoped) return columns;
    return columns.filter((col) => col.key !== userIdField);
  }, [columns, isUserScoped, userIdField]);

  const multiFileFields = useMemo(
    () =>
      visibleFields.filter((f) =>
        ["image", "files", "video", "videos", "documents", "document"].includes(f.type)
      ),
    [visibleFields]
  );
  const hasMultiUpload = multiFileFields.length > 0;

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };

  const applyUserScope = (data = [], extras = {}) => {
    if (!isUserScoped) return { data, extras };
    if (scopedUserId == null) return { data: [], extras: { ...(extras || {}), users: [] } };

    const scopedRows = (data || []).filter(
      (row) => String(row[userIdField]) === String(scopedUserId)
    );
    const nextExtras = { ...(extras || {}) };
    if (Array.isArray(nextExtras.users)) {
      nextExtras.users = nextExtras.users.filter(
        (user) => String(user.value) === String(scopedUserId)
      );
    }
    return { data: scopedRows, extras: nextExtras };
  };

  const filteredSubOptions = useMemo(() => {
    const maps = { ...optionMaps };
    visibleFields.forEach((field) => {
      if (field.filterBy && field.optionsKey) {
        const parentValue = form[field.filterBy];
        const all = optionMaps[field.optionsKey] || [];
        maps[`${field.optionsKey}__filtered`] = parentValue
          ? all.filter((opt) => String(opt.parentId) === String(parentValue))
          : all;
      }
    });
    return maps;
  }, [visibleFields, form, optionMaps]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (isUserScoped && scopedUserId == null) {
        setRows([]);
        setOptionMaps({});
        showToast("No user profile is linked to this login", "error");
        return;
      }

      const listParams =
        scopedUserId != null ? { [userIdField]: scopedUserId } : {};
      const [data, extras] = await Promise.all([
        api.list(listParams),
        loadExtraOptions ? loadExtraOptions() : Promise.resolve({}),
      ]);
      const scoped = applyUserScope(data, extras);
      setRows(scoped.data);
      setOptionMaps(scoped.extras || {});
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    const next = buildEmptyForm(fields);
    if (scopedUserId != null) next[userIdField] = String(scopedUserId);
    setForm(next);
    setImageFile(null);
    setPreview(null);
    setFileUploads({});
    setExistingFiles({});
    setEditingId(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    Object.values(multiFileRefs.current).forEach((input) => {
      if (input) input.value = "";
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleMultiFileChange = (fieldName, e) => {
    const selected = Array.from(e.target.files || []);
    setFileUploads((prev) => ({ ...prev, [fieldName]: selected }));
  };

  const clearInheritedImage = (fieldName) => {
    setFileUploads((prev) => ({ ...prev, [fieldName]: [] }));
    setExistingFiles((prev) => ({ ...prev, [fieldName]: [] }));
    if (multiFileRefs.current[fieldName]) multiFileRefs.current[fieldName].value = "";
  };

  const applyInheritFields = (field, selectedValue, options) => {
    if (!field.inheritFields?.length) return;

    const token = ++inheritTokenRef.current;
    const formUpdates = {};
    const imageUpdates = {};

    if (!selectedValue) {
      field.inheritFields.forEach((spec) => {
        const to = spec.to || spec.from;
        if (spec.type === "image") imageUpdates[to] = null;
        else formUpdates[to] = "";
      });
    } else {
      const resolved = resolveInheritedFromOptions(options, selectedValue, field.inheritFields);
      field.inheritFields.forEach((spec) => {
        const to = spec.to || spec.from;
        const value = resolved[spec.from];
        if (spec.type === "image") {
          imageUpdates[to] = value || null;
        } else {
          formUpdates[to] = value != null && value !== "" ? String(value) : "";
        }
      });
    }

    if (Object.keys(formUpdates).length) {
      setForm((prev) => ({ ...prev, ...formUpdates }));
    }

    Object.entries(imageUpdates).forEach(([to, path]) => {
      if (token !== inheritTokenRef.current) return;
      if (!path) {
        clearInheritedImage(to);
        return;
      }
      setExistingFiles((prev) => ({ ...prev, [to]: asFileList(path) }));
      setFileUploads((prev) => ({ ...prev, [to]: [] }));
      if (multiFileRefs.current[to]) multiFileRefs.current[to].value = "";
    });
  };

  const removeExistingFile = (fieldName, index) => {
    setExistingFiles((prev) => ({
      ...prev,
      [fieldName]: (prev[fieldName] || []).filter((_, i) => i !== index),
    }));
  };

  const validate = () => {
    for (const field of visibleFields) {
      if (!field.required || !isFieldVisible(field, form)) continue;
      const value = form[field.name];
      if (field.type === "multiselect") {
        if (!value || value.length === 0) return `${field.label} is required`;
      } else if (FILE_FIELD_TYPES.has(field.type)) {
        const kept = existingFiles[field.name] || [];
        const uploaded = fileUploads[field.name] || [];
        if (kept.length + uploaded.length === 0 && !editingId) {
          return `${field.label} is required`;
        }
      } else if (value === "" || value === null || value === undefined) {
        return `${field.label} is required`;
      }
    }

    if (isUserScoped && scopedUserId == null) {
      return "No user profile is linked to this login";
    }

    const pending = [
      ...(imageFile ? [imageFile] : []),
      ...Object.values(fileUploads).flat(),
    ];
    for (const file of pending) {
      const sizeError = fileSizeError(file);
      if (sizeError) return sizeError;
    }

    const total = pending.reduce((sum, file) => sum + file.size, 0);
    if (total > MAX_REQUEST_MB * MB) {
      return `Selected files total ${mb(total)} MB, which is over the ${MAX_REQUEST_MB} MB limit for one save. Save them in smaller batches — add a few files, save, then edit the record and add the rest.`;
    }

    return null;
  };

  const withScopedUserId = (payload) => {
    if (!isUserScoped || scopedUserId == null) return payload;
    if (payload instanceof FormData) {
      payload.set(userIdField, String(scopedUserId));
      return payload;
    }
    return { ...payload, [userIdField]: scopedUserId };
  };

  const ownsRecord = (item) => {
    if (!isUserScoped) return true;
    if (scopedUserId == null) return false;
    return String(item?.[userIdField]) === String(scopedUserId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validate();
    if (error) {
      showToast(error, "error");
      return;
    }

    setSaving(true);
    try {
      let saved;

      if (hasMultiUpload || (hasImage && imageField)) {
        const fd = new FormData();
        visibleFields.forEach((field) => {
          if (!isFieldVisible(field, form) && field.name !== userIdField) return;
          if (isVerificationFieldLocked(field)) return;
          if (FILE_FIELD_TYPES.has(field.type)) return;
          if (field.type === "checkbox") {
            fd.append(field.name, form[field.name] ? "1" : "0");
          } else if (field.type === "multiselect") {
            fd.append(field.name, JSON.stringify(form[field.name] || []));
          } else if (field.type === "stringlist") {
            fd.append(field.name, JSON.stringify(parseStringList(form[field.name])));
          } else {
            fd.append(
              field.name,
              form[field.name] === "" || form[field.name] == null ? "" : String(form[field.name])
            );
          }
        });
        withScopedUserId(fd);

        if (hasImage && imageFile && imageField) {
          fd.append(imageField, imageFile);
        }

        multiFileFields.forEach((field) => {
          if (!isFieldVisible(field, form)) return;
          if (isVerificationFieldLocked(field)) return;
          const kept = (existingFiles[field.name] || []).map(basename);
          fd.append(`existing_${field.name}`, JSON.stringify(kept));
          (fileUploads[field.name] || []).forEach((file) => {
            fd.append(field.name, file);
          });
        });

        saved = editingId ? await api.update(editingId, fd) : await api.create(fd);
      } else if (useJson || !hasImage) {
        const payload = {};
        visibleFields.forEach((field) => {
          if (!isFieldVisible(field, form) && field.name !== userIdField) return;
          if (isVerificationFieldLocked(field)) return;
          if (field.type === "checkbox") {
            payload[field.name] = form[field.name] ? 1 : 0;
          } else if (FILE_FIELD_TYPES.has(field.type)) {
            // skip
          } else if (field.type === "multiselect") {
            payload[field.name] = form[field.name] || [];
          } else if (field.type === "stringlist") {
            payload[field.name] = parseStringList(form[field.name]);
          } else if (form[field.name] === "") {
            payload[field.name] = null;
          } else {
            payload[field.name] = form[field.name];
          }
        });

        const body = JSON.stringify(withScopedUserId(payload));
        saved = editingId
          ? await api.update(editingId, body, { json: true })
          : await api.create(body, { json: true });
      } else {
        const fd = new FormData();
        visibleFields.forEach((field) => {
          if (field.type === "file" || field.type === "multiselect") return;
          if (!isFieldVisible(field, form) && field.name !== userIdField) return;
          if (isVerificationFieldLocked(field)) return;
          if (field.type === "checkbox") {
            fd.append(field.name, form[field.name] ? "1" : "0");
          } else {
            fd.append(
              field.name,
              form[field.name] === "" || form[field.name] == null ? "" : String(form[field.name])
            );
          }
        });
        withScopedUserId(fd);
        if (imageFile && imageField) {
          fd.append(imageField, imageFile);
        }
        saved = editingId ? await api.update(editingId, fd) : await api.create(fd);
      }

      if (onAfterSave) {
        await onAfterSave(saved, form, Boolean(editingId));
      }

      showToast(editingId ? `${title} updated successfully` : `${title} saved successfully`);
      resetForm();
      await loadData();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (item) => {
    if (!ownsRecord(item)) {
      showToast("You can only edit your own records", "error");
      return;
    }

    setEditingId(item[idKey]);
    const next = buildEmptyForm(fields);
    const nextExisting = {};

    fields.forEach((field) => {
      if (field.type === "checkbox") {
        next[field.name] = Number(item[field.name]) === 1;
      } else if (field.type === "multiselect") {
        next[field.name] = Array.isArray(item[field.name])
          ? item[field.name].map(String)
          : [];
      } else if (field.type === "stringlist") {
        next[field.name] = stringListToText(item[field.name]);
      } else if (FILE_FIELD_TYPES.has(field.type)) {
        next[field.name] = [];
        nextExisting[field.name] = asFileList(item[field.name]);
      } else if (field.type === "date" && item[field.name] != null) {
        next[field.name] = String(item[field.name]).slice(0, 10);
      } else if (field.type === "datetime-local" && item[field.name] != null) {
        next[field.name] = String(item[field.name]).replace(" ", "T").slice(0, 16);
      } else if (item[field.name] != null) {
        next[field.name] = String(item[field.name]);
      } else {
        next[field.name] = field.defaultValue ?? "";
      }
    });

    if (scopedUserId != null) next[userIdField] = String(scopedUserId);

    setForm(next);
    setExistingFiles(nextExisting);
    setFileUploads({});
    setImageFile(null);

    if (imageField && item[imageField]) {
      setPreview(getImageUrl(item[imageField]));
    } else {
      setPreview(null);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
    Object.values(multiFileRefs.current).forEach((input) => {
      if (input) input.value = "";
    });

    if (loadForEdit) {
      const extra = await loadForEdit(item);
      if (extra) {
        setForm((prev) => {
          const merged = { ...prev, ...extra };
          if (scopedUserId != null) merged[userIdField] = String(scopedUserId);
          return merged;
        });
      }
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    const target = rows.find((row) => String(row[idKey]) === String(id));
    if (target && !ownsRecord(target)) {
      showToast("You can only delete your own records", "error");
      return;
    }

    const confirmed = window.confirm(`Are you sure you want to delete this ${title.toLowerCase()}?`);
    if (!confirmed) return;
    try {
      await api.remove(id);
      showToast(`${title} deleted successfully`);
      if (editingId === id) resetForm();
      await loadData();
    } catch (error) {
      showToast(error.message, "error");
    }
  };

  const renderField = (field) => {
    const locked = isVerificationFieldLocked(field);

    if (field.type === "select") {
      const optionsKey = field.filterBy
        ? `${field.optionsKey}__filtered`
        : field.optionsKey;
      const options =
        filteredSubOptions[optionsKey] ||
        optionMaps[field.optionsKey] ||
        field.options ||
        [];
      return (
        <select
          id={field.name}
          value={form[field.name]}
          disabled={locked}
          onChange={(e) => {
            if (locked) return;
            const value = e.target.value;
            setForm((prev) => {
              const next = { ...prev, [field.name]: value };
              fields.forEach((f) => {
                if (f.filterBy === field.name) next[f.name] = "";
              });
              return next;
            });
            if (field.inheritFields?.length) {
              applyInheritFields(field, value, options);
            }
          }}
        >
          <option value="">{field.placeholder || `Select ${field.label}`}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === "multiselect") {
      const options = optionMaps[field.optionsKey] || [];
      return (
        <DropdownMultiSelect
          id={field.name}
          options={options}
          value={form[field.name] || []}
          disabled={locked}
          placeholder={field.placeholder || `Select ${field.label}`}
          onChange={(next) => {
            if (locked) return;
            setForm((prev) => ({ ...prev, [field.name]: next }));
          }}
        />
      );
    }

    if (field.type === "textarea" || field.type === "stringlist") {
      return (
        <textarea
          id={field.name}
          rows={field.type === "stringlist" ? 4 : 3}
          placeholder={field.placeholder || ""}
          value={form[field.name]}
          readOnly={locked}
          onChange={(e) => {
            if (locked) return;
            setForm((prev) => ({ ...prev, [field.name]: e.target.value }));
          }}
        />
      );
    }

    if (field.type === "richtext") {
      return (
        <ReactQuill
          theme="snow"
          value={form[field.name] || ""}
          readOnly={locked}
          onChange={(value) => {
            if (locked) return;
            setForm((prev) => ({ ...prev, [field.name]: value }));
          }}
          modules={quillModules}
          placeholder={field.placeholder || "Write content..."}
        />
      );
    }

    if (field.type === "checkbox") {
      if (field.yesNo) {
        const isYes = Boolean(form[field.name]);
        return (
          <div className="master-form__radio-group" role="group" aria-labelledby={field.name}>
            <label className="master-form__check">
              <input
                type="checkbox"
                checked={isYes}
                disabled={locked}
                onChange={() => {
                  if (locked) return;
                  setForm((prev) => ({ ...prev, [field.name]: true }));
                }}
              />
              Yes
            </label>
            <label className="master-form__check">
              <input
                type="checkbox"
                checked={!isYes}
                disabled={locked}
                onChange={() => {
                  if (locked) return;
                  setForm((prev) => ({ ...prev, [field.name]: false }));
                }}
              />
              No
            </label>
          </div>
        );
      }

      return (
        <label className="master-form__check">
          <input
            id={field.name}
            type="checkbox"
            checked={Boolean(form[field.name])}
            disabled={locked}
            onChange={(e) => {
              if (locked) return;
              setForm((prev) => ({ ...prev, [field.name]: e.target.checked }));
            }}
          />
          {field.checkLabel || (field.name === "status" ? "Active" : field.label) || "Active"}
        </label>
      );
    }

    if (field.type === "radio") {
      const options = field.options || [];
      return (
        <div className="master-form__radio-group" role="radiogroup" aria-labelledby={field.name}>
          {options.map((opt) => (
            <label key={opt.value} className="master-form__radio">
              <input
                type="radio"
                name={field.name}
                value={opt.value}
                checked={String(form[field.name]) === String(opt.value)}
                disabled={locked}
                onChange={() => {
                  if (locked) return;
                  setForm((prev) => ({ ...prev, [field.name]: opt.value }));
                }}
              />
              {opt.label}
            </label>
          ))}
        </div>
      );
    }

    if (field.type === "file") {
      return (
        <input
          id={field.name}
          ref={fileInputRef}
          type="file"
          accept="image/*"
          disabled={locked}
          onChange={handleImageChange}
        />
      );
    }

    if (
      field.type === "image" ||
      field.type === "files" ||
      field.type === "video" ||
      field.type === "videos" ||
      field.type === "documents" ||
      field.type === "document"
    ) {
      const accept =
        field.accept ||
        (field.type === "video" || field.type === "videos"
          ? "video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm,.avi,.mkv"
          : field.type === "documents" || field.type === "document"
          ? "application/pdf,.pdf"
          : "image/*");
      const multiple =
        field.type === "files" ||
        field.type === "videos" ||
        field.type === "documents";
      const kept = existingFiles[field.name] || [];
      const uploaded = fileUploads[field.name] || [];

      return (
        <div className="master-form__files">
          <input
            id={field.name}
            ref={(el) => {
              multiFileRefs.current[field.name] = el;
            }}
            type="file"
            accept={accept}
            multiple={multiple}
            disabled={locked}
            onChange={(e) => {
              if (locked) return;
              handleMultiFileChange(field.name, e);
            }}
          />
          {kept.length > 0 && (
            <div className="master-form__file-list">
              {kept.map((file, index) => {
                const isImage = field.type === "image" || field.type === "files";
                const isVideo = field.type === "video" || field.type === "videos";
                return (
                  <div key={`${field.name}-existing-${index}`} className="master-form__file-item">
                    {isImage ? (
                      <img src={getImageUrl(file)} alt="" className="master-table__thumb" />
                    ) : isVideo ? (
                      <a href={getImageUrl(file)} target="_blank" rel="noreferrer">
                        {basename(file)}
                      </a>
                    ) : (
                      <a href={getImageUrl(file)} target="_blank" rel="noreferrer">
                        {basename(file)}
                      </a>
                    )}
                    {!locked && (
                      <button
                        type="button"
                        className="master-btn master-btn--ghost"
                        onClick={() => removeExistingFile(field.name, index)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {uploaded.length > 0 && (
            <p className="master-form__hint">{uploaded.length} new file(s) selected</p>
          )}
        </div>
      );
    }

    return (
      <input
        id={field.name}
        type={field.type || "text"}
        placeholder={field.placeholder || ""}
        value={form[field.name]}
        min={field.min}
        step={field.step}
        readOnly={locked}
        onChange={(e) => {
          if (locked) return;
          setForm((prev) => ({ ...prev, [field.name]: e.target.value }));
        }}
      />
    );
  };

  const renderCell = (col, item) => {
    if (col.render) return col.render(item, optionMaps);
    if (col.type === "image") {
      const src = Array.isArray(item[col.key]) ? item[col.key][0] : item[col.key];
      return src ? (
        <img src={getImageUrl(src)} alt="" className="master-table__thumb" />
      ) : (
        <span className="master-table__muted">No image</span>
      );
    }
    if (col.type === "images") {
      const list = asFileList(item[col.key]).slice(0, 3);
      if (!list.length) return <span className="master-table__muted">No image</span>;
      return (
        <div className="master-table__thumbs">
          {list.map((src, i) => (
            <img key={`${col.key}-${i}`} src={getImageUrl(src)} alt="" className="master-table__thumb" />
          ))}
        </div>
      );
    }
    if (col.type === "files") {
      const list = asFileList(item[col.key]);
      if (!list.length) return "—";
      return `${list.length} file(s)`;
    }
    if (col.type === "html") {
      return stripHtml(item[col.key] || "") || "—";
    }
    if (col.type === "status") {
      return Number(item[col.key]) === 1 ? "Active" : "Inactive";
    }
    if (col.type === "money" && item[col.key] != null) {
      return `₹${Number(item[col.key]).toFixed(2)}`;
    }
    const value = item[col.key];
    return value === null || value === undefined || value === "" ? "—" : String(value);
  };

  return (
    <div className="master-page">
      <div className="master-card">
        <div className="master-card__header">
          <h2>{title}</h2>
          <p>{description}</p>
        </div>

        <form className="master-form" onSubmit={handleSubmit}>
          <div className="master-form__grid">
            {formFields.map((field) => {
              if (!isFieldVisible(field, form)) return null;
              const isFileField = FILE_FIELD_TYPES.has(field.type);
              const isFullWidth =
                field.type === "richtext" ||
                field.type === "textarea" ||
                field.type === "stringlist" ||
                field.type === "multiselect" ||
                (Boolean(field.fullWidth) && !isFileField);
              return (
                <div
                  key={field.name}
                  className={`master-form__field ${
                    isFullWidth ? "master-form__field--full" : ""
                  } ${field.type === "richtext" ? "master-form__field--editor" : ""}`}
                >
                  <label htmlFor={field.name}>{field.label}</label>
                  {renderField(field)}
                  {field.hint ? <p className="master-form__hint">{field.hint}</p> : null}
                </div>
              );
            })}
          </div>

          {lockedVerificationFields.length > 0 && (
            <section className="master-verify-section">
              <h3>
                Verification
                <span className="master-verify-section__tag">Readonly</span>
              </h3>

              {verificationBadges.length > 0 && (
                <div className="master-verify-badges">
                  {verificationBadges.map((badge) => {
                    const details = [
                      badge.by ? `By ${badge.by}` : null,
                      badge.date ? `on ${badge.date}` : null,
                    ]
                      .filter(Boolean)
                      .join(" ");
                    return (
                      <div key={badge.key} className="master-verify-badge">
                        <span className="master-verify-badge__mark" aria-hidden="true">
                          ✓
                        </span>
                        <div className="master-verify-badge__body">
                          <strong>{badge.label}</strong>
                          {details ? <span>{details}</span> : null}
                          {badge.certificate ? (
                            <a
                              href={getImageUrl(badge.certificate)}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View certificate
                            </a>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="master-form__grid master-form__grid--verify">
                {lockedVerificationFields.map((field) => (
                  <div key={field.name} className="master-form__field">
                    <label htmlFor={field.name}>{field.label}</label>
                    {renderLockedVerificationField(field)}
                  </div>
                ))}
              </div>
            </section>
          )}

          {preview && (
            <div className="master-form__preview">
              <img src={preview} alt="Preview" />
            </div>
          )}

          <div className="master-form__actions">
            {editingId && (
              <button type="button" className="master-btn master-btn--ghost" onClick={resetForm}>
                Cancel
              </button>
            )}
            <button type="submit" className="master-btn master-btn--primary" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </div>

      <div className="master-card">
        <div className="master-card__header">
          <h3>Saved {title.replace(" Master", "")}s</h3>
          <p>{loading ? "Loading..." : `${rows.length} ${listLabel}`}</p>
        </div>

        <div className="master-table-wrap">
          <table className="master-table">
            <thead>
              <tr>
                <th>#</th>
                {visibleColumns.map((col) => (
                  <th key={col.key || col.label}>{col.label}</th>
                ))}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={visibleColumns.length + 2} className="master-table__empty">
                    No records yet. Add one above.
                  </td>
                </tr>
              )}
              {rows.map((item, index) => (
                <tr key={item[idKey]}>
                  <td>{index + 1}</td>
                  {visibleColumns.map((col) => {
                    const content = renderCell(col, item);
                    if (!isClampedColumn(col)) {
                      return <td key={col.key || col.label}>{content}</td>;
                    }
                    return (
                      <td key={col.key || col.label} className="master-table__desc">
                        <span
                          className="master-table__clamp"
                          title={typeof content === "string" ? content : undefined}
                        >
                          {content}
                        </span>
                      </td>
                    );
                  })}
                  <td>
                    <div className="master-table__actions">
                      <button
                        type="button"
                        className="master-btn master-btn--edit"
                        onClick={() => handleEdit(item)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="master-btn master-btn--delete"
                        onClick={() => handleDelete(item[idKey])}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
